import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Lock, Sparkles, Shield, ArrowRight } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';

/**
 * FeatureGate - Wraps content that requires specific subscription tier features
 * 
 * @param {string} feature - 'ai' | 'autofix' | 'basic'
 * @param {string} featureName - Display name of the feature
 * @param {React.ReactNode} children - Content to show if user has access
 */
export const FeatureGate = ({ feature, featureName, children }) => {
    const { hasFeatureAccess, loading, getTier, error } = useSubscription();

    // Show loading only briefly, then show blocked state if no access
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="feature-gate-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    // If there's an error fetching subscription, show the blocked state (safe default)
    if (error) {
        console.error('FeatureGate: Error fetching subscription:', error);
    }

    if (hasFeatureAccess(feature)) {
        return <>{children}</>;
    }

    // User doesn't have access - show upgrade prompt
    const currentTier = getTier();
    const requiredTier = feature === 'autofix' ? 'Autonomous Protection' : 
                         feature === 'ai' ? 'AI Intelligence' : 'Essential';

    return (
        <div className="p-6" data-testid="feature-gate-blocked">
            <Card className="max-w-2xl mx-auto border-2 border-dashed border-muted-foreground/30">
                <CardHeader className="text-center pb-4">
                    <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center mb-4">
                        <Lock className="w-8 h-8 text-purple-500" />
                    </div>
                    <CardTitle className="text-2xl font-bold">
                        {featureName} - Premium Feature
                    </CardTitle>
                    <CardDescription className="text-base">
                        This feature requires the <Badge variant="secondary" className="mx-1">{requiredTier}</Badge> plan or higher
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Feature Benefits */}
                    <div className="bg-muted/50 rounded-lg p-4">
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-amber-500" />
                            What you'll unlock:
                        </h4>
                        {feature === 'ai' && (
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li className="flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-green-500" />
                                    AI-powered threat detection and analysis
                                </li>
                                <li className="flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-green-500" />
                                    Real-time security risk scoring
                                </li>
                                <li className="flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-green-500" />
                                    Intelligent security recommendations
                                </li>
                                <li className="flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-green-500" />
                                    AI chat assistant for security questions
                                </li>
                            </ul>
                        )}
                        {feature === 'autofix' && (
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li className="flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-green-500" />
                                    Automated threat response and blocking
                                </li>
                                <li className="flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-green-500" />
                                    One-click security remediation
                                </li>
                                <li className="flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-green-500" />
                                    Auto password enforcement
                                </li>
                                <li className="flex items-center gap-2">
                                    <Shield className="w-4 h-4 text-green-500" />
                                    Ransomware behavior detection
                                </li>
                            </ul>
                        )}
                    </div>

                    {/* Current Plan Info */}
                    {currentTier && (
                        <div className="text-center text-sm text-muted-foreground">
                            Your current plan: <Badge variant="outline">{currentTier}</Badge>
                        </div>
                    )}

                    {/* Upgrade CTA */}
                    <div className="text-center">
                        <Link to="/settings">
                            <Button size="lg" className="gap-2" data-testid="upgrade-btn">
                                Upgrade Your Plan
                                <ArrowRight className="w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default FeatureGate;
